import { useEffect, useLayoutEffect, useState } from "preact/hooks";
import { Shield } from "preact-feather";
import {
  base64Decode,
  deriveEncryptionKey,
  publishEncryptionKey,
} from "../encryption.ts";
import { LoginForm, type LoginValues } from "./LoginForm.tsx";
import {
  unregisterServiceWorkers,
  versionedServiceWorkerURL,
} from "../../service_worker/util.ts";
import { version as publicVersion } from "../../../version.json";

/** Values the server templates into the login shell (see auth.html). */
export type AuthConfig = {
  spaceName: string;
  encryptionSalt: string;
  rememberMeDays: number;
  accountManaged: boolean;
};

type SessionProbe =
  | { phase: "login" }
  | { phase: "checking" }
  | { phase: "unlock"; username: string };

export async function activeAccountUsername(
  fetcher: typeof fetch = fetch,
  origin = location.origin,
): Promise<string | undefined> {
  try {
    const response = await fetcher(new URL("/.spaces/api/session", origin), {
      credentials: "include",
      cache: "no-store",
    });
    if (!response.ok) return undefined;
    const body = await response.json();
    return typeof body.username === "string" && body.username
      ? body.username
      : undefined;
  } catch {
    return undefined;
  }
}

export function authReturnTarget(
  baseURI = document.baseURI,
  search = location.search,
  origin = location.origin,
): string {
  const from = new URLSearchParams(search).get("from");
  if (!from) return baseURI;
  try {
    const target = new URL(from, origin);
    return target.origin === origin ? target.toString() : baseURI;
  } catch {
    return baseURI;
  }
}

/**
 * A space's own login page, served at `<space>/.auth`. Unlike the Space
 * Manager's login this one owns two side effects the editor depends on:
 * registering the space's service worker, and deriving the client-encryption
 * key for it.
 */
export function SpaceLogin({ config }: { config: AuthConfig }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const encryptionRequested =
    !!globalThis.localStorage?.getItem("enableEncryption");
  const [session, setSession] = useState<SessionProbe>(() =>
    config.accountManaged && encryptionRequested
      ? { phase: "checking" }
      : { phase: "login" },
  );

  useLayoutEffect(() => {
    document.body.classList.add("pl-login-page");
    return () => document.body.classList.remove("pl-login-page");
  }, []);

  // Registering here — not in the editor — is what makes the login page the
  // first thing that installs a space's worker, so it is already active by the
  // time the editor boots.
  useEffect(() => {
    if (!navigator.serviceWorker) return;
    const workerURL = versionedServiceWorkerURL(publicVersion);
    const scope = workerURL.pathname.slice(
      0,
      workerURL.pathname.lastIndexOf("/") + 1,
    );
    navigator.serviceWorker
      .register(workerURL, {
        type: "module",
        scope,
        updateViaCache: "none",
      })
      .then(() => console.log("Service worker registered"))
      .catch((e) => console.error("Service worker registration failed", e));
  }, []);

  useEffect(() => {
    if (session.phase !== "checking") return;
    void activeAccountUsername().then((username) =>
      setSession(username ? { phase: "unlock", username } : { phase: "login" }),
    );
  }, [session.phase]);

  async function submit(values: LoginValues) {
    setBusy(true);
    setError("");
    const params = new URLSearchParams();
    params.append("username", values.username);
    params.append("password", values.password);
    if (values.rememberMe) params.append("rememberMe", "true");
    const from = new URLSearchParams(location.search).get("from");
    if (from) params.append("from", from);

    try {
      const response = await fetch(".auth", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include",
        body: params.toString(),
      });
      const body = await response.json();
      if (body.status === "error") {
        setError(body.error ?? "登录失败");
        setBusy(false);
        return;
      }

      if (values.clientEncryption || session.phase === "unlock") {
        localStorage.setItem("enableEncryption", "true");
        const key = await deriveEncryptionKey(
          `${values.username}:${values.password}`,
          base64Decode(config.encryptionSalt),
        );
        if (!(await publishEncryptionKey(key, config.accountManaged))) {
          // Without a worker holding the key the editor would boot, find no
          // key and bounce straight back here — say so instead of looping.
          setError(
            "客户端加密需要 Service Worker，但当前浏览器无法使用。" +
              "请刷新后重试，或关闭客户端加密再登录。",
          );
          setBusy(false);
          return;
        }
      } else {
        localStorage.removeItem("enableEncryption");
      }

      location.href = body.redirect;
    } catch {
      setError("无法连接服务器，请检查网络后重试。");
      setBusy(false);
    }
  }

  async function disableClientEncryption() {
    setBusy(true);
    setError("");
    try {
      localStorage.removeItem("enableEncryption");
      await unregisterServiceWorkers();
      location.replace(authReturnTarget());
    } catch {
      setError("无法关闭本机缓存加密，请刷新后重试。");
      setBusy(false);
    }
  }

  if (session.phase === "checking") {
    return (
      <main class="pl-login-scene">
        <div class="pl-login-clouds is-back" aria-hidden="true" />
        <div class="pl-login-clouds is-front" aria-hidden="true" />
        <div class="pl-login-land" aria-hidden="true" />
        <section class="pl-login-panel pl-login-check" aria-live="polite">
          <Shield size={22} />
          <strong>正在确认登录状态</strong>
          <span>马上就好...</span>
        </section>
      </main>
    );
  }

  const unlocking = session.phase === "unlock";

  return (
    <LoginForm
      title={unlocking ? "解锁本机缓存" : <>登录 {config.spaceName}</>}
      subtitle={
        unlocking
          ? "账号仍保持登录，只需解锁这台设备上的加密缓存"
          : "原生 Markdown 会一直保存在你自己的空间"
      }
      error={error}
      busy={busy}
      rememberMeDays={unlocking ? undefined : config.rememberMeDays}
      clientEncryption={!config.accountManaged && !unlocking}
      clientEncryptionHint="密钥只保存在内存中。手机关闭页面或切到后台后可能需要再次输入密码；个人 NAS 通常无需开启。"
      initialClientEncryption={encryptionRequested}
      initialUsername={unlocking ? session.username : ""}
      usernameReadOnly={unlocking}
      submitLabel={unlocking ? "解锁并继续" : "进入我的笔记"}
      onSubmit={(values) => void submit(values)}
    >
      {unlocking && (
        <div class="pl-login-disable-encryption">
          <button
            type="button"
            disabled={busy}
            onClick={() => void disableClientEncryption()}
          >
            关闭本机缓存加密并继续
          </button>
          <span>NAS 上的 Markdown 不受影响，关闭后不再频繁要求密码。</span>
        </div>
      )}
    </LoginForm>
  );
}
